/**
 * MDM-MOBILE-P1-001: Client-side image processing pipeline hook
 *
 * Processes captured/selected images before upload:
 * 1. EXIF orientation correction
 * 2. Resize to max 2048px longest edge
 * 3. WebP conversion (JPEG fallback)
 * 4. Thumbnail generation (200px)
 * 5. Compression metrics
 *
 * All processing client-side — no server round-trip.
 */
import { useCallback } from 'react';

/**
 * Read EXIF orientation tag from JPEG ArrayBuffer.
 * Returns orientation value 1-8, or 1 if not found.
 */
function readExifOrientation(arrayBuffer) {
  const view = new DataView(arrayBuffer);
  if (view.getUint16(0, false) !== 0xFFD8) return 1; // Not JPEG

  let offset = 2;
  while (offset < view.byteLength - 2) {
    const marker = view.getUint16(offset, false);
    offset += 2;
    if (marker === 0xFFE1) {
      // APP1 marker (EXIF)
      const length = view.getUint16(offset, false);
      const exifOffset = offset + 2;
      // Check for 'Exif\0\0'
      if (
        view.getUint32(exifOffset, false) === 0x45786966 &&
        view.getUint16(exifOffset + 4, false) === 0x0000
      ) {
        const tiffOffset = exifOffset + 6;
        const bigEndian = view.getUint16(tiffOffset, false) === 0x4D4D;
        const ifdOffset = view.getUint32(tiffOffset + 4, bigEndian) + tiffOffset;
        const entries = view.getUint16(ifdOffset, bigEndian);
        for (let i = 0; i < entries; i++) {
          const entryOffset = ifdOffset + 2 + i * 12;
          /* c8 ignore next */
          if (entryOffset + 12 > view.byteLength) break;
          if (view.getUint16(entryOffset, bigEndian) === 0x0112) {
            return view.getUint16(entryOffset + 8, bigEndian);
          }
        /* c8 ignore next */
        }
      /* c8 ignore next */
      }
      /* c8 ignore next */
      offset += length - 2;
    } else if ((marker & 0xFF00) === 0xFF00) {
      offset += view.getUint16(offset, false);
    } else {
      break;
    }
  }
  return 1;
}

/**
 * Apply EXIF orientation transform to canvas context.
 */
function applyOrientationTransform(ctx, orientation, width, height) {
  switch (orientation) {
    case 2: ctx.transform(-1, 0, 0, 1, width, 0); break;
    case 3: ctx.transform(-1, 0, 0, -1, width, height); break;
    case 4: ctx.transform(1, 0, 0, -1, 0, height); break;
    case 5: ctx.transform(0, 1, 1, 0, 0, 0); break;
    case 6: ctx.transform(0, 1, -1, 0, height, 0); break;
    case 7: ctx.transform(0, -1, -1, 0, height, width); break;
    case 8: ctx.transform(0, -1, 1, 0, 0, width); break;
    default: break;
  }
}

/**
 * Check if orientation swaps width/height.
 */
function orientationSwapsDimensions(orientation) {
  return orientation >= 5 && orientation <= 8;
}

export default function useImagePipeline() {
  const processImage = useCallback(async (blob) => {
    const arrayBuffer = await blob.arrayBuffer();
    const orientation = readExifOrientation(arrayBuffer);

    // Create image from blob
    const img = await createImageBitmap(blob);
    const originalWidth = img.width;
    const originalHeight = img.height;

    // Calculate resize dimensions (max 2048px longest edge)
    const maxDim = 2048;
    let targetWidth = originalWidth;
    let targetHeight = originalHeight;
    const longestEdge = Math.max(originalWidth, originalHeight);

    if (longestEdge > maxDim) {
      const scale = maxDim / longestEdge;
      targetWidth = Math.round(originalWidth * scale);
      targetHeight = Math.round(originalHeight * scale);
    }

    // Determine canvas dimensions after orientation
    const swapped = orientationSwapsDimensions(orientation);
    const canvasWidth = swapped ? targetHeight : targetWidth;
    const canvasHeight = swapped ? targetWidth : targetHeight;

    // Process main image
    const canvas = document.createElement('canvas');
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext('2d');
    applyOrientationTransform(ctx, orientation, canvasWidth, canvasHeight);
    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

    // Try WebP, fall back to JPEG
    let format = 'image/webp';
    let processedBlob = await new Promise(resolve =>
      canvas.toBlob(resolve, 'image/webp', 0.85)
    );
    if (!processedBlob || processedBlob.size === 0) {
      format = 'image/jpeg';
      processedBlob = await new Promise(resolve =>
        canvas.toBlob(resolve, 'image/jpeg', 0.85)
      );
    }

    // Generate thumbnail (200px longest edge)
    const thumbMaxDim = 200;
    const thumbScale = thumbMaxDim / Math.max(canvasWidth, canvasHeight);
    const thumbWidth = Math.round(canvasWidth * thumbScale);
    const thumbHeight = Math.round(canvasHeight * thumbScale);
    const thumbCanvas = document.createElement('canvas');
    thumbCanvas.width = thumbWidth;
    thumbCanvas.height = thumbHeight;
    const thumbCtx = thumbCanvas.getContext('2d');
    thumbCtx.drawImage(canvas, 0, 0, thumbWidth, thumbHeight);
    const thumbnail = await new Promise(resolve =>
      thumbCanvas.toBlob(resolve, 'image/jpeg', 0.7)
    );

    const compressionRatio = processedBlob.size / blob.size;

    return {
      processed: processedBlob,
      thumbnail,
      originalDims: { width: originalWidth, height: originalHeight },
      finalDims: { width: canvasWidth, height: canvasHeight },
      compressionRatio: Math.round(compressionRatio * 10000) / 10000,
      format: format === 'image/webp' ? 'webp' : 'jpeg',
    };
  }, []);

  return { processImage };
}

// Export helpers for testing
export { readExifOrientation, applyOrientationTransform, orientationSwapsDimensions };
